import React, { useState } from "react";
import { Box, Container, Paper, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CircularProgress from "@mui/material/CircularProgress";
import * as XLSX from "xlsx";
import HCPTable from "../../Components/HCPMasterData/HCPTable";
import HCPForm from "../../Components/HCPMasterData/HCPForm";
import { usePermission } from "../../hooks/usePermission";
import ImportHCP from "../../Components/HCPMasterData/ImportHCP";
import { CreateHCPMasterDataPermission } from "../../constants/permissions";
import { useGetAllWithoutLimit } from "../../hooks/useDoctorMasterData";
import { useCity } from "../../hooks/useCity";
import { useCountry } from "../../hooks/useCountry";

const HCPMasterData = () => {
  const { data: canCreateHCPMasterData } = usePermission({
    permissionName: CreateHCPMasterDataPermission,
  });

  const { data: hcpData } = useGetAllWithoutLimit();
  const { data: cityData } = useCity();
  const { data: countryData } = useCountry();
  const [isExporting, setIsExporting] = useState(false);

  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    national_id: "",
    first_name: "",
    family_name: "",
    email: "",
    phone_number: "",
    specialty: "",
    organization: "",
    medical_license: "",
    APO_number: "",
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({
      national_id: "",
      first_name: "",
      family_name: "",
      email: "",
      phone_number: "",
      specialty: "",
      organization: "",
      medical_license: "",
      APO_number: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);

      // Allow UI to update before heavy processing
      await new Promise((resolve) => setTimeout(resolve, 0));

      const headers = [
        "First Name",
        "Family Name",
        "Email",
        "Phone Number",
        "Specialty",
        "Country",
        "City",
        "Industry Area",
        "Medical License",
        "Organization",
        "National ID",
        "APO Number",
      ];

      const data =
        hcpData?.map((hcp) => [
          hcp.first_name || "",
          hcp.family_name || "",
          hcp.email || "",
          hcp.phone_number || "",
          hcp.specialty || "",
          countryData?.find((c) => c.id === hcp.country_id)?.name || "N/A",
          cityData?.find((c) => c.id === hcp.city_id)?.name || "N/A",
          hcp.industry_area || "",
          hcp.medical_license || "",
          hcp.organization || "",
          hcp.national_id || "",
          hcp.APO_number || "",
        ]) || [];

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
      XLSX.utils.book_append_sheet(wb, ws, "HCP Master Data");

      XLSX.writeFile(wb, "hcp_master_data.xlsx");
    } catch (error) {
      console.error("Error exporting HCP data:", error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Typography variant="h4" component="h1">
            HCP Master Data
          </Typography>
          <div className="flex items-center space-x-2">
            {canCreateHCPMasterData && (
              <div className="flex items-center space-x-2">
                <button
                  className="bg-blueColor/90 hover:bg-blueColor transition-colors duration-200 text-white rounded-md px-4 py-2 flex items-center gap-2"
                  onClick={handleClickOpen}
                >
                  <AddIcon />
                  Add New HCP
                </button>
                <ImportHCP />
              </div>
            )}
            <button
              data-track="export-hcp-button"
              data-module="HCPs"
              className={`bg-green-600 transition-colors duration-200 text-white rounded-md px-4 py-2 flex items-center gap-2 ${
                isExporting
                  ? "cursor-not-allowed bg-green-600/60"
                  : "hover:bg-green-700"
              }`}
              onClick={handleExport}
              disabled={isExporting}
            >
              {isExporting ? (
                <>
                  <CircularProgress size={20} color="inherit" />
                  Exporting...
                </>
              ) : (
                <>
                  <FileDownloadIcon />
                  Export
                </>
              )}
            </button>
          </div>
        </Box>

        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <HCPTable />
        </Paper>

        <HCPForm
          open={open}
          onClose={handleClose}
          formData={formData}
          onInputChange={handleInputChange}
        />
      </Box>
    </Container>
  );
};

export default HCPMasterData;
